import { createStaticAssistant } from './assistant.js';
import { createDialogController } from './dialogs.js';
import {
  createElement,
  listen,
  requiredElement,
  setHidden,
  toText
} from './dom.js';
import {
  renderChapters,
  renderRail,
  renderSummary,
  updateShellProject
} from './document-renderer.js';

/**
 * Creates the ArchAI 06 view over an existing deliverables store.
 * `onOpenExport` receives `{phase:'open'|'confirm', format?, state, setStatus?, close?}`.
 * `onPreviewImage` may return a URL, Blob, or `{fileUrl|src,title,caption,note}`.
 */
export function createDeliverablesView({
  store,
  onPreviewImage,
  onPreviewReport,
  onOpenExport,
  onReset,
  onToast
}) {
  if (!store?.getState || !store?.subscribe) throw new TypeError('A deliverables store is required.');

  const elements = {
    summary: requiredElement('deliverableSummary'),
    rail: requiredElement('chapterRail'),
    list: requiredElement('chapterList'),
    loading: requiredElement('loadingState'),
    empty: requiredElement('emptyState'),
    error: requiredElement('errorState'),
    errorMessage: requiredElement('errorMessage'),
    selectionSummary: requiredElement('selectionSummary'),
    draftStatus: requiredElement('draftStatus'),
    assistantContent: requiredElement('assistantContent'),
    assistantInput: requiredElement('assistantInput'),
    assistantSend: requiredElement('assistantSend'),
    contextPanel: requiredElement('contextPanel'),
    closeContext: requiredElement('closeContext'),
    toastRegion: requiredElement('toastRegion'),
    workspace: document.querySelector('.workspace'),
    exportButton: requiredElement('exportButton'),
    previewButton: requiredElement('previewButton'),
    resetButton: requiredElement('resetDraftButton'),
    selectAllButton: requiredElement('selectAllButton'),
    deselectAllButton: requiredElement('deselectAllButton')
  };
  const removers = [];
  const toastTimers = new Set();
  let chapterNodes = new Map();
  let activeChapterId = '';
  let chapterObserver = null;
  let destroyed = false;

  function notify(message, type = 'info') {
    const normalized = toText(message);
    if (!normalized || destroyed) return;
    const tone = ['info', 'success', 'warning', 'error'].includes(type) ? type : 'info';
    const toast = createElement('div', {
      className: `toast toast-${tone}`,
      text: normalized,
      attrs: { role: tone === 'error' ? 'alert' : 'status' }
    });
    elements.toastRegion.append(toast);
    const timer = window.setTimeout(() => {
      toast.remove();
      toastTimers.delete(timer);
    }, 3600);
    toastTimers.add(timer);
    try {
      onToast?.(normalized, tone);
    } catch {
      // A host notification hook must not break the local UI.
    }
  }

  async function submitExport({ format, setStatus, close }) {
    if (!onOpenExport) {
      setStatus('导出执行器尚未接入。当前编排草稿不会受到影响。', 'warning');
      notify('导出执行器尚未接入', 'warning');
      return;
    }
    setStatus(`正在准备 ${format.toUpperCase()} 导出快照…`);
    try {
      const state = store.getState();
      const result = await onOpenExport({
        phase: 'confirm',
        format,
        state,
        document: state.document,
        setStatus,
        close
      });
      if (result === false) {
        setStatus('导出已取消。');
        return;
      }
      setStatus('导出任务已完成。', 'success');
      close();
      notify(`${format.toUpperCase()} 已导出`, 'success');
    } catch (error) {
      setStatus(toText(error?.message, '导出失败，请检查图片资源后重试。'), 'error');
      notify('导出失败，项目数据未被修改', 'error');
    }
  }

  const dialogs = createDialogController({
    imageModal: requiredElement('imageModal'),
    exportModal: requiredElement('exportModal'),
    onExportSubmit: submitExport
  });

  const assistant = createStaticAssistant({
    content: elements.assistantContent,
    input: elements.assistantInput,
    sendButton: elements.assistantSend,
    getState: () => store.getState()
  });

  function setActiveChapter(chapterId) {
    if (!chapterId || activeChapterId === chapterId) return;
    activeChapterId = chapterId;
    for (const button of elements.rail.querySelectorAll('.chapter-nav-item')) {
      const active = button.dataset.chapterId === chapterId;
      button.classList.toggle('active', active);
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }
  }

  function activateChapter(chapterId) {
    setActiveChapter(chapterId);
    const target = chapterNodes.get(chapterId);
    if (!target) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  }

  function observeChapters() {
    chapterObserver?.disconnect();
    chapterObserver = null;
    if (!('IntersectionObserver' in window) || !chapterNodes.size) return;
    const root = window.matchMedia?.('(max-width: 760px)').matches ? null : elements.workspace;
    chapterObserver = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting)
        .sort((left, right) => right.intersectionRatio - left.intersectionRatio);
      const chapterId = visible[0]?.target?.dataset?.chapterId;
      if (chapterId) setActiveChapter(chapterId);
    }, { root, rootMargin: '-8% 0px -62% 0px', threshold: [0.1, 0.3, 0.55] });
    chapterNodes.forEach(node => chapterObserver.observe(node));
  }

  async function previewImage(item) {
    dialogs.openImage(item);
    if (!onPreviewImage) return;
    try {
      const result = await onPreviewImage(item, store.getState());
      if (result != null) dialogs.updateImage(result, item);
    } catch (error) {
      notify(toText(error?.message, '图片预览失败'), 'error');
    }
  }

  const renderCallbacks = {
    onSelectChapter: (chapterId, selected) => store.toggleChapter(chapterId, selected),
    onMoveChapter: (chapterId, offset) => {
      store.moveChapterBy(chapterId, offset);
      notify(offset < 0 ? '章节已上移' : '章节已下移');
    },
    onUpdateChapter: (chapterId, patch) => store.updateChapter(chapterId, patch),
    onSelectItem: (chapterId, itemId, selected) => store.toggleItem(chapterId, itemId, selected),
    onMoveItem: (chapterId, itemId, offset) => {
      store.moveItemBy(chapterId, itemId, offset);
      notify(offset < 0 ? '成果项已上移' : '成果项已下移');
    },
    onUpdateItem: (chapterId, itemId, patch) => store.updateItem(chapterId, itemId, patch),
    onPreviewImage: previewImage
  };

  function showError(error) {
    elements.errorMessage.textContent = toText(error?.message || error, '成果渲染失败，请稍后重试。');
    setHidden(elements.loading, true);
    setHidden(elements.empty, true);
    setHidden(elements.error, false);
    setHidden(elements.list, true);
    elements.list.setAttribute('aria-busy', 'false');
  }

  function render(state = store.getState()) {
    if (destroyed) return;
    try {
      const chapters = state.document?.chapters || [];
      if (!chapters.some(chapter => chapter.id === activeChapterId)) activeChapterId = chapters[0]?.id || '';
      updateShellProject(state);
      renderSummary(elements.summary, state);
      renderRail(elements.rail, chapters, activeChapterId, activateChapter);
      const rows = renderChapters(elements.list, chapters, renderCallbacks);
      chapterNodes = new Map(rows.map(row => [row.chapterId, row.node]));
      elements.selectionSummary.textContent = `${state.selection.selectedChapters} / ${state.selection.chapterCount} 章，${state.selection.selectedItems} / ${state.selection.itemCount} 项已选择`;
      elements.draftStatus.textContent = state.revision ? `编排修订 ${state.revision}` : '编排草稿';
      elements.list.setAttribute('aria-busy', 'false');
      setHidden(elements.loading, true);
      setHidden(elements.error, true);
      setHidden(elements.empty, chapters.length > 0);
      setHidden(elements.list, chapters.length === 0);
      observeChapters();
    } catch (error) {
      showError(error);
    }
  }

  async function resetDraft() {
    const nextState = store.reset();
    try {
      await onReset?.(nextState);
      notify('成果编排已恢复为默认状态');
    } catch (error) {
      notify(toText(error?.message, '草稿重置后的保存失败'), 'error');
    }
  }

  async function previewReport() {
    try {
      await onPreviewReport?.(store.getState().document, store.getState());
    } catch (error) {
      notify(toText(error?.message, '汇报预览打开失败'), 'error');
    }
  }

  async function openExport() {
    dialogs.openExport();
    try {
      await onOpenExport?.({
        phase: 'open',
        format: dialogs.getExportFormat(),
        state: store.getState(),
        document: store.getState().document,
        setStatus: dialogs.setExportStatus
      });
    } catch (error) {
      dialogs.setExportStatus(toText(error?.message, '导出检查失败。'), 'error');
    }
  }

  removers.push(store.subscribe(render));
  removers.push(listen(elements.selectAllButton, 'click', () => {
    store.selectAll();
    notify('已选择全部可用成果');
  }));
  removers.push(listen(elements.deselectAllButton, 'click', () => {
    store.selectNone();
    notify('已取消全部成果选择');
  }));
  removers.push(listen(elements.resetButton, 'click', resetDraft));
  removers.push(listen(elements.previewButton, 'click', previewReport));
  removers.push(listen(elements.exportButton, 'click', openExport));
  removers.push(listen(elements.closeContext, 'click', () => setHidden(elements.contextPanel, true)));
  render();

  return {
    render,
    openImage: previewImage,
    openExport,
    showToast: notify,
    showError,
    openAssistant: () => setHidden(elements.contextPanel, false),
    destroy() {
      destroyed = true;
      chapterObserver?.disconnect();
      dialogs.destroy();
      assistant.destroy();
      removers.splice(0).forEach(remove => remove());
      toastTimers.forEach(timer => window.clearTimeout(timer));
      toastTimers.clear();
    }
  };
}
